import React, { useState, useEffect } from 'react';
import {
    Container, Typography, TextField, Button, Grid, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    InputAdornment, IconButton, Paper, Box, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Visibility, VisibilityOff, Delete, History } from '@mui/icons-material';
import api, { signOut } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PasswordRequirements from './PasswordRequirements';
import { validatePassword } from '../utils/passwordValidation';

const ProfilePage = () => {
    const { user: authUser } = useAuth();
    const [user, setUser] = useState(null);
    const [editedUser, setEditedUser] = useState({});
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);

    // Data Management State
    const [openDeleteOrdersDialog, setOpenDeleteOrdersDialog] = useState(false);
    const [openDeleteLogsDialog, setOpenDeleteLogsDialog] = useState(false);
    const [openDeletePurchaseOrdersDialog, setOpenDeletePurchaseOrdersDialog] = useState(false);
    const [openDeleteStockTransfersDialog, setOpenDeleteStockTransfersDialog] = useState(false);
    const [deleteStartDate, setDeleteStartDate] = useState('');
    const [deleteEndDate, setDeleteEndDate] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [showDeletePassword, setShowDeletePassword] = useState(false);
    const [deleteType, setDeleteType] = useState(''); // 'orders', 'logs', 'purchaseOrders', or 'stockTransfers'

    // View Logs State
    const [openViewLogsDialog, setOpenViewLogsDialog] = useState(false);
    const [viewLogsStartDate, setViewLogsStartDate] = useState('');
    const [viewLogsEndDate, setViewLogsEndDate] = useState('');
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
            setEditedUser(response.data);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            setError('Failed to fetch user profile');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditDialogOpen = () => {
        setEditedUser({ ...user });
        setOpenEditDialog(true);
    };

    const handleEditDialogClose = () => {
        setOpenEditDialog(false);
    };

    const handlePasswordDialogOpen = () => {
        setOpenPasswordDialog(true);
    };

    const handlePasswordDialogClose = () => {
        setOpenPasswordDialog(false);
        setNewPassword('');
        setConfirmPassword('');
        setOldPassword('');
    };

    const handleEditChange = (e) => {
        setEditedUser({ ...editedUser, [e.target.name]: e.target.value });
    };

    const handleEditSubmit = async () => {
        try {
            const response = await api.put('/auth/update-profile', { ...editedUser, password });
            setUser(response.data);
            setOpenEditDialog(false);
            setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to update profile', severity: 'error' });
        }
    };

    const handlePasswordSubmit = async () => {
        // Validate new password
        const { isValid, errors } = validatePassword(newPassword);
        if (!isValid) {
            setSnackbar({
                open: true,
                message: `Password does not meet requirements: ${errors.join(', ')}`,
                severity: 'error'
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            setSnackbar({ open: true, message: 'Passwords do not match', severity: 'error' });
            return;
        }

        try {
            const response = await api.put('/auth/update-password', { oldPassword, newPassword });
            handlePasswordDialogClose();
            setSnackbar({
                open: true,
                message: response.data?.message || 'Password updated successfully. Please login again.',
                severity: 'success'
            });

            // If token was invalidated, sign out after delay
            if (response.data?.tokenInvalidated) {
                setTimeout(() => {
                    signOut();
                }, 2000);
            }
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to update password', severity: 'error' });
        }
    };

    // Data Management Handlers
    const handleDeleteOrdersDialogOpen = () => {
        setDeleteType('orders');
        setDeleteStartDate('');
        setDeleteEndDate('');
        setDeletePassword('');
        setOpenDeleteOrdersDialog(true);
    };

    const handleDeleteLogsDialogOpen = () => {
        setDeleteType('logs');
        setDeleteStartDate('');
        setDeleteEndDate('');
        setDeletePassword('');
        setOpenDeleteLogsDialog(true);
    };

    const handleDeletePurchaseOrdersDialogOpen = () => {
        setDeleteType('purchaseOrders');
        setDeleteStartDate('');
        setDeleteEndDate('');
        setDeletePassword('');
        setOpenDeletePurchaseOrdersDialog(true);
    };

    const handleDeleteStockTransfersDialogOpen = () => {
        setDeleteType('stockTransfers');
        setDeleteStartDate('');
        setDeleteEndDate('');
        setDeletePassword('');
        setOpenDeleteStockTransfersDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setOpenDeleteOrdersDialog(false);
        setOpenDeleteLogsDialog(false);
        setOpenDeletePurchaseOrdersDialog(false);
        setOpenDeleteStockTransfersDialog(false);
        setDeleteStartDate('');
        setDeleteEndDate('');
        setDeletePassword('');
        setDeleteType('');
    };

    const handleDeleteSubmit = async () => {
        if (!deleteStartDate || !deleteEndDate || !deletePassword) {
            setSnackbar({ open: true, message: 'Please fill all fields', severity: 'error' });
            return;
        }

        try {
            let endpoint;
            let typeLabel;
            switch (deleteType) {
                case 'orders':
                    endpoint = '/orders/delete-old';
                    typeLabel = 'orders';
                    break;
                case 'logs':
                    endpoint = '/auth/logs/delete-old';
                    typeLabel = 'logs';
                    break;
                case 'purchaseOrders':
                    endpoint = '/purchase-orders/delete-old';
                    typeLabel = 'purchase orders';
                    break;
                case 'stockTransfers':
                    endpoint = '/stock-transfers/delete-old';
                    typeLabel = 'stock transfers';
                    break;
                default:
                    endpoint = '/orders/delete-old';
                    typeLabel = 'records';
            }

            const response = await api.post(endpoint, {
                startDate: deleteStartDate,
                endDate: deleteEndDate,
                password: deletePassword
            });

            handleDeleteDialogClose();
            setSnackbar({
                open: true,
                message: response.data.message || `Successfully deleted ${response.data.deletedCount} ${typeLabel}`,
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || `Failed to delete ${deleteType}`,
                severity: 'error'
            });
        }
    };

    // View Logs Handlers
    const handleViewLogsDialogOpen = () => {
        setViewLogsStartDate('');
        setViewLogsEndDate('');
        setLogs([]);
        setOpenViewLogsDialog(true);
    };

    const handleViewLogsDialogClose = () => {
        setOpenViewLogsDialog(false);
        setViewLogsStartDate('');
        setViewLogsEndDate('');
        setLogs([]);
    };

    const handleViewLogs = async () => {
        if (!viewLogsStartDate || !viewLogsEndDate) {
            setSnackbar({ open: true, message: 'Please select both start and end dates', severity: 'error' });
            return;
        }

        setLogsLoading(true);
        try {
            const response = await api.get('/auth/logs');

            // Filter logs by date range
            const start = new Date(viewLogsStartDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(viewLogsEndDate);
            end.setHours(23, 59, 59, 999);

            const filteredLogs = response.data.filter(log => {
                const logDate = new Date(log.createdAt);
                return logDate >= start && logDate <= end;
            });

            setLogs(filteredLogs);

            if (filteredLogs.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'No logs found in the selected date range',
                    severity: 'info'
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to fetch logs',
                severity: 'error'
            });
        } finally {
            setLogsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <Container>
                <CircularProgress />
                <Typography>Loading user profile...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Typography color="error">{error}</Typography>
                <Button onClick={fetchUserProfile}>Retry</Button>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container>
                <Typography>No user profile found</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Profile Section */}
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}
                >
                    My Profile
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Manage your account information and preferences
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Name
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {user.name}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Email
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {user.email}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Phone Number
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {user.phoneNumber}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Buttons Section */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={handleEditDialogOpen}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                }
                            }}
                        >
                            Update Profile
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            onClick={handlePasswordDialogOpen}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(156, 39, 176, 0.4)',
                                }
                            }}
                        >
                            Update Password
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={signOut}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderWidth: 2,
                                '&:hover': {
                                    borderWidth: 2,
                                }
                            }}
                        >
                            Sign Out
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Data Management Section (Owner Only) */}
            {authUser?.role === 'owner' && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        mt: 4,
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}
                    >
                        Data Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Permanently delete old data from the system
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="outlined"
                                color="error"
                                fullWidth
                                startIcon={<Delete />}
                                onClick={handleDeleteOrdersDialogOpen}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        bgcolor: 'error.light',
                                        color: 'white'
                                    }
                                }}
                            >
                                Delete Old Orders
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Delete completed orders older than 2 months
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="outlined"
                                color="warning"
                                fullWidth
                                startIcon={<Delete />}
                                onClick={handleDeleteLogsDialogOpen}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        bgcolor: 'warning.light',
                                        color: 'white'
                                    }
                                }}
                            >
                                Delete Old Logs
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Delete logs older than 7 days (auto-delete after 4 days)
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="outlined"
                                color="info"
                                fullWidth
                                startIcon={<History />}
                                onClick={handleViewLogsDialogOpen}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        bgcolor: 'info.light',
                                        color: 'white'
                                    }
                                }}
                            >
                                View Logs
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                View system logs for a date range
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="outlined"
                                color="success"
                                fullWidth
                                startIcon={<Delete />}
                                onClick={handleDeletePurchaseOrdersDialogOpen}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        bgcolor: 'success.light',
                                        color: 'white'
                                    }
                                }}
                            >
                                Delete Old Purchase Orders
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Delete purchase orders older than 6 months
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                startIcon={<Delete />}
                                onClick={handleDeleteStockTransfersDialogOpen}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        bgcolor: 'secondary.light',
                                        color: 'white'
                                    }
                                }}
                            >
                                Delete Old Stock Transfers
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Delete stock transfers older than 6 months
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Update Profile Dialog */}
            <Dialog
                open={openEditDialog}
                onClose={handleEditDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600 }}>
                    Update Profile
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Name"
                        name="name"
                        value={editedUser.name}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        value={editedUser.email}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Phone Number"
                        name="phoneNumber"
                        value={editedUser.phoneNumber}
                        onChange={handleEditChange}
                        onFocus={(e) => e.target.select()}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showPassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleEditDialogClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        color="primary"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Update Password Dialog */}
            <Dialog
                open={openPasswordDialog}
                onClose={handlePasswordDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 600 }}>
                    Update Password
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showOldPassword ? 'text' : 'password'}
                        label="Current Password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowOldPassword(!showOldPassword)}
                                        edge="end"
                                    >
                                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showNewPassword ? 'text' : 'password'}
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1 }}
                    />

                    {/* Password Requirements Display */}
                    <PasswordRequirements password={newPassword} />

                    <TextField
                        fullWidth
                        margin="normal"
                        type="password"
                        label="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={confirmPassword && newPassword !== confirmPassword}
                        helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handlePasswordDialogClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePasswordSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Update Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Orders Dialog */}
            <Dialog
                open={openDeleteOrdersDialog}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 600 }}>
                    Delete Old Orders
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will permanently delete all completed orders older than 2 months in the selected date range. This action cannot be undone.
                    </Alert>
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="Start Date"
                        value={deleteStartDate}
                        onChange={(e) => setDeleteStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="End Date"
                        value={deleteEndDate}
                        onChange={(e) => setDeleteEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showDeletePassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                                        edge="end"
                                    >
                                        {showDeletePassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleDeleteDialogClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        variant="contained"
                        color="error"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Delete Orders
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Logs Dialog */}
            <Dialog
                open={openDeleteLogsDialog}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 600 }}>
                    Delete Old Logs
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Logs automatically delete after 4 days. This will manually delete logs older than 7 days in the selected range.
                    </Alert>
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="Start Date"
                        value={deleteStartDate}
                        onChange={(e) => setDeleteStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="End Date"
                        value={deleteEndDate}
                        onChange={(e) => setDeleteEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showDeletePassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                                        edge="end"
                                    >
                                        {showDeletePassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleDeleteDialogClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        variant="contained"
                        color="warning"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Delete Logs
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Purchase Orders Dialog */}
            <Dialog
                open={openDeletePurchaseOrdersDialog}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600 }}>
                    Delete Old Purchase Orders
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will permanently delete all purchase orders older than 6 months in the selected date range. Stock levels will NOT be affected as this is historical data. This action cannot be undone.
                    </Alert>
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="Start Date"
                        value={deleteStartDate}
                        onChange={(e) => setDeleteStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="End Date"
                        value={deleteEndDate}
                        onChange={(e) => setDeleteEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showDeletePassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                                        edge="end"
                                    >
                                        {showDeletePassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleDeleteDialogClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        variant="contained"
                        color="success"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Delete Purchase Orders
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Stock Transfers Dialog */}
            <Dialog
                open={openDeleteStockTransfersDialog}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 600 }}>
                    Delete Old Stock Transfers
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will permanently delete all stock transfers older than 6 months in the selected date range. Stock levels will NOT be affected as this is historical data. This action cannot be undone.
                    </Alert>
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="Start Date"
                        value={deleteStartDate}
                        onChange={(e) => setDeleteStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type="date"
                        label="End Date"
                        value={deleteEndDate}
                        onChange={(e) => setDeleteEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        type={showDeletePassword ? 'text' : 'password'}
                        label="Enter your password to confirm"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                                        edge="end"
                                    >
                                        {showDeletePassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleDeleteDialogClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Delete Stock Transfers
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Logs Dialog */}
            <Dialog
                open={openViewLogsDialog}
                onClose={handleViewLogsDialogClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', fontWeight: 600 }}>
                    View System Logs
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                value={viewLogsStartDate}
                                onChange={(e) => setViewLogsStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                type="date"
                                label="End Date"
                                value={viewLogsEndDate}
                                onChange={(e) => setViewLogsEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="info"
                                onClick={handleViewLogs}
                                disabled={logsLoading}
                                sx={{
                                    height: '56px',
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                {logsLoading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
                            </Button>
                        </Grid>
                    </Grid>

                    {logs.length > 0 && (
                        <TableContainer sx={{ maxHeight: 500, mt: 2 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light' }}>Date & Time</TableCell>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light' }}>User</TableCell>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light' }}>Action</TableCell>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light' }}>Details</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow key={log._id} hover>
                                            <TableCell>{formatDate(log.createdAt)}</TableCell>
                                            <TableCell>
                                                {log.user?.name || 'Unknown User'}
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {log.user?.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'primary.main'
                                                    }}
                                                >
                                                    {log.action}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {log.details || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {!logsLoading && logs.length === 0 && viewLogsStartDate && viewLogsEndDate && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                                No logs found for the selected date range
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleViewLogsDialogClose} sx={{ textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for Alerts */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                {snackbar.message}
                </Alert>
            </Snackbar>
            </Container>
    );
};

export default ProfilePage;